"use client";

import { VACATION_PROTECTION_DAYS } from "@/lib/constants";
import { getVacationGuardStart, mergeBackToBackVacations } from "@/lib/utils";
import { Team, Tournament } from "@/types";
import { differenceInCalendarDays, format, startOfDay } from "date-fns";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";

interface TeamVacationPeriodsProps {
  team: Team;
  tournament: Tournament;
}

/**
 * TeamVacationPeriods - Always lists a team's set vacation periods (ongoing and
 * upcoming) in the team drawer. Each period also shows the vacation guard window
 * (the protection days before the vacation starts during which the team cannot be
 * challenged), so other players can see the actual complete unavailable stretch.
 */
export function TeamVacationPeriods({
  team,
  tournament,
}: TeamVacationPeriodsProps) {
  const t = useTranslations();
  // Defer time-based filtering to after mount to avoid hydration mismatch.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const protectionDays =
    tournament.vacationProtectionDays ?? VACATION_PROTECTION_DAYS;

  // Merge back-to-back vacations into single ranges, then show only those that
  // haven't fully ended yet, soonest first.
  const periods = useMemo(() => {
    if (!mounted || !team.vacations) return [];
    const today = startOfDay(new Date());
    return mergeBackToBackVacations(team.vacations).filter(
      (range) => startOfDay(range.end) >= today,
    );
  }, [mounted, team.vacations]);

  if (periods.length === 0) return null;

  const today = startOfDay(new Date());

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-xs font-medium text-amber-500/80 uppercase tracking-wider">
          {t("teams.vacationPeriodsTitle")}
        </h3>
        <div className="flex-1 h-px bg-amber-500/20" />
      </div>

      <div className="rounded-xl bg-[#0a0c10]/60 border border-amber-500/10 p-3 space-y-2.5">
        {periods.map((vacation) => {
          const { start, end } = vacation;
          const isActive =
            startOfDay(start) <= today && startOfDay(end) >= today;
          const daysRemaining = Math.max(
            0,
            differenceInCalendarDays(startOfDay(end), today) + 1,
          );
          const guardStart = getVacationGuardStart(
            vacation,
            team.vacations ?? [],
            protectionDays,
          );

          return (
            <div key={vacation.id} className="flex items-start gap-2.5">
              <span className="text-lg leading-none mt-0.5" aria-hidden>
                {isActive ? "🏖️" : "📅"}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-amber-400">
                    {format(start, "dd.MM.yyyy HH:mm")} –{" "}
                    {format(end, "dd.MM.yyyy HH:mm")}
                  </span>
                  {isActive && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-300 bg-amber-500/15 rounded px-1.5 py-0.5">
                      {t("myTeam.vacation.active")}
                    </span>
                  )}
                </div>

                {isActive && (
                  <div className="text-xs text-amber-400/80 mt-0.5">
                    {t("teams.vacationDaysRemaining", {
                      days: daysRemaining,
                    })}
                  </div>
                )}

                {guardStart && (
                  <div className="flex items-center gap-1 text-xs text-white/50 mt-0.5">
                    <span aria-hidden>🛡️</span>
                    <span>
                      {t("teams.vacationGuardNote", {
                        date: format(guardStart, "dd.MM.yyyy HH:mm"),
                        days: protectionDays,
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
